import React from 'react'
import { useMediaQuery } from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'

import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'
import { INTRO_SCROLL_BUTTON, RECAP_INTRO_SECTION } from 'utils/testing/testIDs'

import { ACTIONS, OPTIONS, baseAnalyticsOptions } from '../analytics/constants'
import { getImagePath, getRecapQuarter } from '../helpers'
import { RECAP_XS_BREAKPOINT } from '../styles'
import {
  Container,
  Content,
  DownButton,
  LgBarkRectangle,
  LgLightOak,
  MdBeigeRectangle,
  MdDuskTriangle,
  MobileShapes,
  ShapeContainer,
  SmLightOakCircle,
  SquirrelAvatar,
  SubTitle,
  Title,
} from './styled-components'

const Intro = ({
  beneficiaryId,
  beneficiaryName,
  endsAt,
  handleScrollNext,
  startsAt,
}: {
  beneficiaryId: string
  beneficiaryName: string
  endsAt: string
  handleScrollNext: () => void
  startsAt: string
}): React.ReactElement => {
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()
  const inViewContainerRef = useAnalyticsComponentViewed({
    threshold: 0.4,
    action: ACTIONS.SCREEN_VIEW,
    payload: {
      ...baseAnalyticsOptions,
      action: 'Viewed',
      beneficiaryId,
      context: OPTIONS.INTRO.context,
      event_description: 'Early Quarterly Recaps Screen Viewed',
      index_position: OPTIONS.INTRO.position,
      object: 'Screen',
      object_name: 'earlyQuarterlyRecaps',
    },
  })

  const isMobile = useMediaQuery({
    query: `(max-width: ${RECAP_XS_BREAKPOINT}px)`,
  })

  const { recapTimeframe, releaseDate } = getRecapQuarter({ endsAt, startsAt })

  return (
    <Container data-testid={RECAP_INTRO_SECTION} ref={inViewContainerRef}>
      <Content>
        <SquirrelAvatar src={getImagePath('squirrel-avatar')} />
        <Title>
          <span>
            {getRemoteString({
              key: 'early.recap.intro.header.web.part1.1variable',
              variables: [beneficiaryName],
            })}
          </span>
          {isMobile ? <br /> : <span> </span>}
          <span>
            {getRemoteString({
              key: 'early.recap.intro.header.web.part2.1variable',
              variables: [recapTimeframe],
            })}
          </span>
        </Title>
        <SubTitle>
          <StyledRemoteString
            stringKey={'early.recap.intro.subheader.2variable.markdown'}
            variables={[beneficiaryName, releaseDate]}
          />
        </SubTitle>
        <DownButton
          role="button"
          aria-label="Scroll Next Section Button"
          data-testid={INTRO_SCROLL_BUTTON}
          onClick={handleScrollNext}
        >
          <AcornsIcon icon={AcornsIcon.Icon.UtilityArrowLeft} width={24} />
        </DownButton>
      </Content>
      <ShapeContainer>
        <LgLightOak src={getImagePath('large-light-oak')} />
        <LgBarkRectangle src={getImagePath('large-bark-rectangle')} />
        <MobileShapes>
          <MdDuskTriangle src={getImagePath('medium-dusk-triangle')} />
          <SmLightOakCircle src={getImagePath('small-circle')} />
          <MdBeigeRectangle src={getImagePath('medium-beige-rectangle')} />
        </MobileShapes>
      </ShapeContainer>
    </Container>
  )
}

export { Intro }
